import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SERVICE_TYPES,
  FREQUENCIES,
  PHONE_DISPLAY,
  PHONE_TEL,
  EMAIL,
} from "@/lib/brand";
import { api, formatApiError } from "@/lib/api";

const blankForm = {
  name: "",
  phone: "",
  email: "",
  addressOrArea: "",
  serviceType: "",
  frequency: "",
  message: "",
};

export default function QuoteSection() {
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onChange = (k) => (e) => update(k)(e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const required = ["name", "phone", "email", "addressOrArea", "serviceType", "frequency", "message"];
    for (const k of required) {
      if (!form[k] || String(form[k]).trim() === "") {
        toast.error("Please fill out every field so we can send a proper quote.");
        return;
      }
    }

    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setSubmitted(true);
      setForm(blankForm);
      toast.success("Thanks! We got your request and will be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err) || "Sorry — something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="relative py-24 md:py-32 bg-sky-50" data-testid="quote-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-sky-700">
              Ready to Get Started?
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-slate-900 tracking-tight">
              Request your free quote.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Tell us what you need — a quick clean, a recurring plan, a handyman project, or all
              of the above. We&apos;ll follow up with a simple, honest quote.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={`tel:${PHONE_TEL}`}
                className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-slate-100 hover:border-sky-200 hover:-translate-y-0.5 transition-all"
                data-testid="quote-call-link"
              >
                <div className="w-11 h-11 rounded-xl bg-sky-500 text-white flex items-center justify-center">
                  <Phone size={20} weight="bold" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Call or text</div>
                  <div className="text-base font-semibold text-slate-900">{PHONE_DISPLAY}</div>
                </div>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-slate-100 hover:border-sky-200 hover:-translate-y-0.5 transition-all"
                data-testid="quote-email-link"
              >
                <div className="w-11 h-11 rounded-xl bg-sky-500 text-white flex items-center justify-center">
                  <EnvelopeSimple size={20} weight="bold" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Email us</div>
                  <div className="text-base font-semibold text-slate-900 break-all">{EMAIL}</div>
                </div>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl shadow-xl shadow-sky-900/5 border border-slate-100 p-6 sm:p-10">
              {submitted ? (
                <div
                  className="flex flex-col items-center text-center py-10"
                  data-testid="quote-success"
                >
                  <div className="w-16 h-16 rounded-full bg-sky-500/10 text-sky-600 flex items-center justify-center">
                    <CheckCircle size={36} weight="fill" />
                  </div>
                  <h3 className="mt-5 text-2xl font-display font-bold text-slate-900">
                    Thanks — we&apos;ve got it!
                  </h3>
                  <p className="mt-3 text-slate-600 max-w-md">
                    A real human will be in touch within one business day to follow up on your
                    quote. If it&apos;s urgent, give us a ring at {PHONE_DISPLAY}.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-6 text-sm font-semibold text-sky-600 hover:text-sky-700"
                    data-testid="quote-send-another"
                  >
                    Send another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Your Name" htmlFor="name">
                      <Input
                        id="name"
                        value={form.name}
                        onChange={onChange("name")}
                        placeholder="Jane Doe"
                        data-testid="quote-input-name"
                      />
                    </Field>
                    <Field label="Phone Number" htmlFor="phone">
                      <Input
                        id="phone"
                        value={form.phone}
                        onChange={onChange("phone")}
                        placeholder="423-555-0100"
                        type="tel"
                        data-testid="quote-input-phone"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Email" htmlFor="email">
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        placeholder="you@example.com"
                        data-testid="quote-input-email"
                      />
                    </Field>
                    <Field label="Address or General Area" htmlFor="addressOrArea">
                      <Input
                        id="addressOrArea"
                        value={form.addressOrArea}
                        onChange={onChange("addressOrArea")}
                        placeholder="East Brainerd, Cleveland, etc."
                        data-testid="quote-input-area"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Type of Service">
                      <Select
                        value={form.serviceType}
                        onValueChange={update("serviceType")}
                      >
                        <SelectTrigger data-testid="quote-select-service">
                          <SelectValue placeholder="Pick one" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICE_TYPES.map((s) => (
                            <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="How Often?">
                      <Select
                        value={form.frequency}
                        onValueChange={update("frequency")}
                      >
                        <SelectTrigger data-testid="quote-select-frequency">
                          <SelectValue placeholder="Pick a schedule" />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCIES.map((f) => (
                            <SelectItem key={f} value={f} data-testid={`freq-option-${f}`}>
                              {f}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label="Tell us what you need help with" htmlFor="message">
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={onChange("message")}
                      rows={5}
                      placeholder="E.g. bi-weekly house clean for a 3-bedroom, plus hang 4 pictures and mount a TV."
                      data-testid="quote-input-message"
                    />
                  </Field>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white font-semibold text-base px-8 py-4 rounded-full transition-all shadow-lg shadow-sky-500/30 hover:-translate-y-0.5 disabled:translate-y-0"
                    data-testid="quote-submit-button"
                  >
                    {submitting ? "Sending…" : "Request My Quote"}
                    {!submitting && <PaperPlaneTilt size={18} weight="bold" />}
                  </button>

                  <p className="text-xs text-slate-500 text-center">
                    No spam. We only use your info to respond to your request.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={htmlFor}
        className="text-xs font-semibold uppercase tracking-widest text-slate-600"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}
